import { PreviewTableImage } from "@/features";
import React, {
  ChangeEvent,
  FC,
  FocusEvent,
  type ReactElement,
  ReactNode,
  useEffect,
  useState,
} from "react";
import styles from "./CartDealerTable.module.scss";
import { numberWithSpaces } from "@/shared/helpers/AddSpacesToNumber";
import {
  addProductionItemsCart,
  addRemainsItemsCart,
  changeDealersIdToShow,
  changeItemQuantity,
  fetchProductionCart,
  fetchRemainsCart,
  setCanCreateOrder,
  setCartLoader,
  CartProductKeysEnum,
  CartProductType,
  CartType,
  ChangeItemToCart,
  ItemToCart,
  TotalInfoType,
  fetchCartSummary,
  clearCartFilters,
  fetchCartFilters,
  deleteCartItems,
} from "@/entities/cart";
import { useAppDispatch, useAppSelector } from "@/shared/hooks";
import clsx from "clsx";
import { Tooltip } from "antd";
import CustomCheckbox from "@/shared/ui/checkbox/CustomCheckbox";
import ArrowIcon from "./assets/ArrowIcon.svg";
import { IDealer } from "@/entities/dealer";
import { fetchDealerRecommendations } from "@/entities/recommendation/slice";
import WarningIcon from "@/shared/assets/warningIcon.svg";
import { TotalValuesType } from "./types";

interface TableItemProps {
  targetFilial: Partial<IDealer>;
  products: CartProductType[];
  selectedProducts: string[];
  dealerViewCheckboxHandler: (id: string, rows: string[]) => void;
  cartType: CartType;
  color: "white" | "gray";
  lastProduct?: boolean;
  changeTotalValues: (type: CartType, id: string, value: TotalInfoType) => void;
  totalValues: TotalValuesType;
  fetchFunction: () => Promise<void>;
}

type TableRowProps = {
  filial?: Partial<IDealer> | null;
  product: CartProductType;
};

type FilialsCountInfoType = { id: string; quantity: number; remains: number };
const tableHeaders = Object.keys(CartProductKeysEnum).filter(
  (i) => i !== "filialCol",
) as [keyof typeof CartProductKeysEnum];
const TableItem: FC<TableItemProps> = React.memo(
  ({
    targetFilial,
    products,
    selectedProducts,
    dealerViewCheckboxHandler,
    cartType = "production",
    color = "white",
    lastProduct = false,
    changeTotalValues,
    totalValues,
    fetchFunction,
  }) => {
    const dispatch = useAppDispatch();
    const [filialsTotalQuantity, setFilialsTotalQuantity] = useState<number>(0);
    const [filialsCount, setFilialsCount] = useState<FilialsCountInfoType[]>(
      [],
    );
    const [isInputChanged, setIsInputChanged] = useState<boolean>(false);

    const [showFilials, setShowFilials] = useState<boolean>(false);

    const { mainFilial, currentDealer } = useAppSelector(
      (state) => state.dealers,
    );
    const {
      dealersProdIdsToShow,
      dealersRemainsIdsToShow,
      deleteMode,
      canCreateOrder,
      totalRemainsCount,
    } = useAppSelector((state) => state.cart);
    const filialIds =
      cartType === "production"
        ? dealersProdIdsToShow
        : dealersRemainsIdsToShow;

    const handleChange = (
      e: ChangeEvent<HTMLInputElement>,
      product: CartProductType,
      filial: Partial<IDealer> | null = null,
    ) => {
      if (+e.target.value > +e.target.max) {
        e.target.value = e.target.max;
      }
      setIsInputChanged(true);
      if (filial) {
        const newArr = filialsCount.filter((fc) => fc.id !== product.vendorId);
        newArr.push({
          id: product.vendorId,
          quantity: +e.target.value,
          remains: product.remainQuantity,
        });
        setFilialsCount(newArr);
      }
    };
    const currentTotals = totalValues[cartType];

    const total = Object.keys(currentTotals).reduce(
      (acc: TotalInfoType, item) => {
        const { totalPrice, totalQuantity } = acc;
        const newTotalPrice = totalPrice + currentTotals[item].totalPrice;
        const newTotalQuantity =
          totalQuantity + currentTotals[item].totalQuantity;

        acc.totalPrice = newTotalPrice;
        acc.totalQuantity = newTotalQuantity;

        return acc;
      },
      { totalPrice: 0, totalQuantity: 0 } as TotalInfoType,
    );

    const handleBlur = async (
      e: FocusEvent<HTMLInputElement>,
      product: CartProductType,
      filial: Partial<IDealer>,
    ) => {
      if (isInputChanged) {
        const hasBasketId = product.basketFilials.find(
          (i) => i.dealerId === filial?.id,
        );

        if (hasBasketId) {
          const changedProductArr: ChangeItemToCart[] = [
            {
              itemId: hasBasketId.basketId,
              quantity: +e.target.value,
              filialId: filial.id,
            },
          ];
          if (+e.target.value === 0) {
            await dispatch(
              deleteCartItems({ basketIds: [hasBasketId.basketId] }),
            );
            dispatch(clearCartFilters());
            await dispatch(fetchCartFilters({ type: cartType }));
          } else {
            await dispatch(changeItemQuantity({ items: changedProductArr }));
          }
        } else {
          const changedProductArr: ItemToCart[] = [
            {
              vendorId: product.vendorId,
              quantity: +e.target.value,
              filialId: filial.id,
            },
          ];
          cartType === "production"
            ? await dispatch(
                addProductionItemsCart({ items: changedProductArr }),
              )
            : await dispatch(addRemainsItemsCart({ items: changedProductArr }));
        }
        await dispatch(fetchCartSummary(cartType === "production" ? 1 : 2));
        dispatch(setCartLoader(true));
        await fetchFunction();
        dispatch(fetchDealerRecommendations(""));

        setIsInputChanged(false);
      }
      dispatch(setCartLoader(false));
    };

    const getValue = (
      product: CartProductType,
      headerName: keyof CartProductType,
      filial: Partial<IDealer> | null = null,
    ) => {
      const usedQuantity = product.basketFilials.reduce((acc, bf) => {
        if (targetFilial.id !== bf.dealerId) {
          acc += bf.quantity;
        }
        return acc;
      }, 0);

      const quantityRemains = product.remainQuantity - usedQuantity;

      const priceToUse = currentDealer?.isExporter
        ? product.exporterPrice
        : product.dealerPrice;

      const currentFilialInfo =
        filialsCount && filialsCount.find((f) => f.id === product.vendorId);

      let maxValue = 999_999;
      if (cartType === "remains" && currentFilialInfo) {
        maxValue = quantityRemains;
      }

      const value = currentFilialInfo
        ? currentFilialInfo?.quantity
        : filialsTotalQuantity;

      switch (headerName) {
        case "dealerPrice":
          if (filial) return <></>;
          return numberWithSpaces(product[headerName]);
        case "totalPrice":
          if (filial)
            return numberWithSpaces(
              currentFilialInfo ? priceToUse * currentFilialInfo?.quantity : 0,
            );
          return numberWithSpaces(currentFilialInfo?.quantity * priceToUse);

        case "image":
          if (filial) {
            return filial.shortName;
          }
          return (
            <PreviewTableImage src={product.image || null} isSmall={true} />
          );

        case "fullName":
          if (filial) {
            return <></>;
          }
          return (
            <div className={styles.fullNameBlock}>
              {product.orders.length > 0 && (
                <Tooltip
                  arrow={false}
                  trigger={"click"}
                  placement="bottom"
                  overlayStyle={{ minWidth: "320px" }}
                  overlayInnerStyle={{
                    backgroundColor: "white",
                  }}
                  title={
                    <div className={styles.orderTTInfo}>
                      <p>Обратите внимание! Диски содержатся в заказе (-ах)</p>
                      {product.orders.map((order) => {
                        return (
                          <span key={order.number}>{`${
                            order.number
                          } - ${order.value.toLocaleString()} шт.`}</span>
                        );
                      })}
                    </div>
                  }
                >
                  <WarningIcon />
                </Tooltip>
              )}
              {product[headerName]}
            </div>
          );

        case "quantity":
          return (
            <div className={styles.tdInput}>
              <input
                id={filial ? filial.id : product.vendorId}
                type="number"
                onClick={(e) => {
                  e.stopPropagation();
                }}
                onChange={(e) => handleChange(e, product, targetFilial)}
                onBlur={(e) => handleBlur(e, product, targetFilial)}
                min={0}
                max={maxValue >= 0 ? maxValue : 0}
                onKeyDown={(e) =>
                  ["e", "E", "+", "-"].includes(e.key) && e.preventDefault()
                }
                value={
                  (cartType === "remains" && product.remainQuantity > 0) ||
                  cartType === "production"
                    ? value.toString()
                    : "0"
                }
                onWheel={() => (document.activeElement as HTMLElement).blur()}
                className={clsx(styles.input, {
                  [styles.shortInput]: filial,
                  [styles.errorInput]:
                    cartType === "remains" &&
                    product.remainQuantity > 0 &&
                    (maxValue < value ||
                      (!filial &&
                        currentFilialInfo?.quantity > product.remainQuantity)),
                })}
                disabled={product.remainQuantity < 1 && cartType === "remains"}
              />
              {!filial &&
                product.remainQuantity > 0 &&
                currentFilialInfo?.quantity > product.remainQuantity &&
                cartType === "remains" && (
                  <div className={styles.errorMessage}>
                    <p>Количество товара ограничено!1</p>
                    <p>Доступно только {product.remainQuantity} шт.</p>
                  </div>
                )}
            </div>
          );
        default:
          if (filial) return <></>;
          return product[headerName] as ReactNode;
      }
    };

    const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      setShowFilials((prev) => !prev);
      dispatch(
        changeDealersIdToShow({
          id: targetFilial.id,
          type: cartType,
        }),
      );
    };

    function TableRow({
      filial = mainFilial,
      product,
    }: TableRowProps): ReactElement {
      const productBasketId = product.basketFilials.find(
        (bf) => bf.dealerId === targetFilial.id,
      ).basketId;
      return (
        <Tooltip
          key={product.vendorId + product.vendorId}
          placement="bottom"
          title={
            cartType === "production" &&
            !product.canOrdered &&
            "Данная позиция недоступна для заказа!"
          }
          overlayClassName={styles.tooltip}
        >
          <tr
            className={clsx(styles[color], styles.small, {
              [styles.selectedRow]: selectedProducts.includes(productBasketId),
              [styles.blocked]:
                cartType === "production" && !product.canOrdered,
              [styles.disabledRow]:
                cartType === "remains" && !product.remainQuantity,
            })}
          >
            {tableHeaders &&
              tableHeaders.map((headerName, index) => {
                return (
                  <td key={headerName + index} className={styles[headerName]}>
                    {getValue(product, headerName as keyof CartProductType)}
                  </td>
                );
              })}

            <td className={styles.lastColumn}>
              <>
                {!filial && deleteMode && (
                  <CustomCheckbox
                    id={productBasketId || product.vendorId}
                    data={selectedProducts}
                    handleClickCheck={dealerViewCheckboxHandler}
                  />
                )}
              </>
            </td>
          </tr>
        </Tooltip>
      );
    }
    useEffect(() => {
      if (filialIds && filialIds.includes(targetFilial.id)) {
        setShowFilials(true);
      } else {
        setShowFilials(false);
      }
    }, [filialIds]);
    useEffect(() => {
      if (products.length) {
        const filialsInfo = products.reduce(
          (acc: FilialsCountInfoType[], product) => {
            const current = product.basketFilials.find(
              (basketFilial) => basketFilial.dealerId === targetFilial.id,
            );

            if (
              (cartType === "remains" &&
                product.remainQuantity > 0 &&
                product.remainQuantity < current.quantity) ||
              !totalRemainsCount
            ) {
              dispatch(setCanCreateOrder(false));
            }
            const value: FilialsCountInfoType = {
              id: product.vendorId,
              quantity: current ? current.quantity : 0,
              remains: product.remainQuantity,
            };
            acc.push(value);
            return acc;
          },
          [] as FilialsCountInfoType[],
        );
        setFilialsCount(filialsInfo);
      }
    }, [products, cartType, currentDealer, totalRemainsCount]);
    useEffect(() => {
      const totalQuantities = filialsCount.reduce(
        (
          acc: { totalQuantity: number; totalRemainsQuantity: number },
          product,
        ) => {
          acc.totalQuantity += product.quantity;
          acc.totalRemainsQuantity += product.remains;
          return acc;
        },
        { totalQuantity: 0, totalRemainsQuantity: 0 },
      );

      const totalPrice = products.reduce((acc: number, product) => {
        const priceToUse = currentDealer?.isExporter
          ? product.exporterPrice
          : product.dealerPrice;

        const value =
          filialsCount.find((f) => f.id === product.vendorId)?.quantity || 0;
        acc += value * priceToUse;
        return acc;
      }, 0);

      const { totalQuantity, totalRemainsQuantity } = totalQuantities;

      setFilialsTotalQuantity(totalQuantity);
      const totalInfo: TotalInfoType = {
        totalQuantity,
        totalPrice,
        totalRemainsQuantity,
      };

      changeTotalValues(cartType, targetFilial.id, totalInfo);
    }, [
      filialsCount,
      products?.length,
      addProductionItemsCart,
      addRemainsItemsCart,
    ]);
    return (
      <>
        <tr className={clsx(styles.big, styles[color])}>
          {!!products?.length &&
            tableHeaders.map((headerName) => {
              const totalQuantity =
                currentTotals[targetFilial.id]?.totalQuantity || 0;

              return (
                <>
                  <td
                    className={clsx(styles[headerName])}
                    key={headerName + headerName + "ss"}
                  >
                    {headerName === "image" && targetFilial.shortName}
                    {headerName === "quantity" && currentTotals && (
                      <div
                        className={clsx({
                          [styles.error]:
                            cartType === "remains" &&
                            !canCreateOrder &&
                            totalRemainsCount,
                        })}
                      >
                        {totalQuantity}
                        {cartType === "remains" &&
                          !canCreateOrder &&
                          totalRemainsCount && (
                            <div className={styles.errorMessage}>
                              <p>Количество товара ограничено!</p>
                            </div>
                          )}
                      </div>
                    )}
                    {headerName === "totalPrice" &&
                      numberWithSpaces(
                        currentTotals[targetFilial.id]?.totalPrice,
                      ) + " ₽"}
                  </td>
                </>
              );
            })}
          <td>
            <div
              className={clsx(styles.arrow, {
                [styles.arrowDown]: showFilials,
              })}
              onClick={handleClick}
            >
              {<ArrowIcon width={17} />}
            </div>
          </td>
        </tr>
        {showFilials &&
          !!products?.length &&
          products.map((product) => {
            return (
              <React.Fragment key={product.vendorId + product.vendorCode}>
                {
                  TableRow({
                    filial: null,
                    product: product,
                  }) as React.ReactNode
                }
              </React.Fragment>
            );
          })}
        {lastProduct && (
          <tr className={clsx(styles.cartTotalRow)}>
            {tableHeaders.map((productValue) => {
              return (
                <td
                  className={clsx(styles[productValue])}
                  key={productValue + productValue + "ss"}
                >
                  {productValue === "image" && "Итого:"}
                  {productValue === "quantity" && total && (
                    <div>{total.totalQuantity}</div>
                  )}
                  {productValue === "totalPrice" &&
                    total &&
                    numberWithSpaces(total.totalPrice) + " ₽"}
                </td>
              );
            })}
            <td> </td>
          </tr>
        )}
      </>
    );
  },
);

export default TableItem;
